import React from 'react'
import { StripeCardElement } from '@stripe/stripe-js'
import { useStripe, useElements as useStripeElements, CardElement } from '@stripe/react-stripe-js'
import { DEVICE, pxToRem } from 'theme'
import {
  useAuth,
  useCardForm,
  CardFieldName,
  IPaymentCard,
  useUserCards,
  USER_CARDS_QUERY,
  useSetupPaymentMethod,
  useDeletePaymentMethod,
} from '@modmd/data'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { useToaster } from 'utils/useToaster'
import { Box } from 'components/Layout'
import { Text } from 'components/Typography'
import Button from 'components/Button'
import { Dialog } from 'components/Dialog'
import Card from 'components/Card'
import CardInput from 'components/CardInput'
import { PaymentCard } from 'components/PaymentCard'

export const Billing = () => {
  const userAuth = useAuth()
  const stripe = useStripe()
  const elements = useStripeElements()
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const { setToastMessage } = useToaster()
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = React.useState(false)
  const [currentCard, setCurrentCard] = React.useState<IPaymentCard>()
  const { data: cardsData, refetch: refetchCards } = useUserCards()
  const [deletePaymentMethod, { loading: isDeletePaymentMethodLoading }] = useDeletePaymentMethod({
    refetchQuery: USER_CARDS_QUERY,
  })
  const [setupPaymentMethod] = useSetupPaymentMethod()
  const cardForm = useCardForm({
    onSubmit: async (_, { resetForm }) => {
      if (!stripe || !elements) {
        return
      }
      const { data } = await setupPaymentMethod()
      if (data?.setupUserPaymentMethod) {
        const result = await stripe.confirmCardSetup(data.setupUserPaymentMethod, {
          payment_method: {
            card: elements.getElement(CardElement) as StripeCardElement,
            billing_details: {
              name: `${userAuth.data.User.firstName || ''} ${userAuth.data.User.lastName || ''}`,
            },
          },
        })
        if (result.error) {
          setToastMessage(result.error.message as string, 'danger')
        } else {
          setIsModalOpen(false)
          resetForm()
          void refetchCards()
          setToastMessage('Card successfully added', 'success')
        }
      }
    },
  })
  const handleDialogDismiss = React.useCallback(() => {
    setIsModalOpen(false)
    cardForm.resetForm()
  }, [cardForm])

  const cards = (cardsData?.me?.User.Cards ?? []) as IPaymentCard[]
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="2rem">
        <Text>Credit Cards</Text>
        <Box>
          <Button
            onClick={() => {
              setIsModalOpen(true)
            }}
          >
            Add Card
          </Button>
        </Box>
      </Box>
      <Box display="grid" gridGap="1rem">
        {cards.map((card) => (
          <PaymentCard
            key={card.stripeId}
            card={card}
            actionButton={() => (
              <Button
                disabled={isDeletePaymentMethodLoading}
                colorVariant="danger"
                appearance="outlined"
                onClick={() => {
                  setCurrentCard(card)
                  setIsRemoveModalOpen(true)
                }}
              >
                Remove
              </Button>
            )}
          />
        ))}
      </Box>
      <Dialog
        variant={isMobile ? 'fullscreen' : 'center'}
        maxWidth={pxToRem(500)}
        isOpen={isModalOpen}
        onDismiss={handleDialogDismiss}
      >
        <Box flexDirection="column" width="100%">
          <Card.Title onDismiss={handleDialogDismiss}>Add card</Card.Title>
          <Card.Content>
            <CardInput
              name={CardFieldName.CARD}
              label="Card"
              onChange={(event) => {
                void cardForm.setFieldValue(
                  CardFieldName.CARD,
                  event.complete || event.error?.message
                )
              }}
              errorMessage={
                cardForm.touched?.card && cardForm.errors?.card ? cardForm.errors?.card : undefined
              }
              onBlur={cardForm.handleBlur}
            />
          </Card.Content>
          <Card.Actions>
            <Button appearance="outlined" mr="1rem" onClick={handleDialogDismiss}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                await cardForm.submitForm()
              }}
              disabled={cardForm.isSubmitting}
            >
              Save Card
            </Button>
          </Card.Actions>
        </Box>
      </Dialog>
      <Dialog
        variant={isMobile ? 'fullscreen' : 'center'}
        maxWidth={pxToRem(500)}
        isOpen={isRemoveModalOpen}
        onDismiss={() => setIsRemoveModalOpen(false)}
      >
        <Box flexDirection="column" width="100%">
          <Card.Title onDismiss={() => setIsRemoveModalOpen(false)}>Remove Credit Card</Card.Title>
          <Card.Content>
            <Text>Are you sure you want to remove this credit card?</Text>
          </Card.Content>
          <Card.Actions>
            <Button mr="1rem" onClick={() => setIsRemoveModalOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={isDeletePaymentMethodLoading}
              colorVariant="danger"
              mr="1rem"
              onClick={async () => {
                const response = await deletePaymentMethod({
                  variables: {
                    stripeId: currentCard?.stripeId as string,
                  },
                })
                if (!response.errors) {
                  setToastMessage('Card successfully removed', 'success')
                  setIsRemoveModalOpen(false)
                }
              }}
            >
              Remove
            </Button>
          </Card.Actions>
        </Box>
      </Dialog>
    </Box>
  )
}
